import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {
  ConfirmationService,
  DateFormatService,
  FileSaverService,
  Icon,
  PrimengDialogDirective,
  ProgressBarComponent,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
  ToastService,
} from '@web/shared/ui';
import { AdminBoxCollectionItem } from '../../models/admin-box-collection-item';
import { AdminBoxCollectionFilter } from '../../models/admin-box-collection-filter';
import { AdminBoxListItemAction } from '../../enums/admin-box-list-item-action';
import { AdminBoxListCollectionAction } from '../../enums/admin-box-list-collection-action';
import { AdminBoxListAction } from '../../enums/admin-box-list-action';
import { AdminBoxDataSourceService } from '../../services/admin-box-data-source.service';
import { defer, EMPTY, expand, last, map, Observable, of, switchMap, takeWhile, tap, timer } from 'rxjs';
import { Route, RouteParam, YYYY_MM_DD_HH_MM_SS } from '@web/common';
import { Guid, isNil, once } from '@web/utils';
import { AdminBoxListCustomCellTemplateComponent } from '../../components/admin-box-list-custom-cell-template/admin-box-list-custom-cell-template.component';
import { AdminBoxStatusProviderService } from '../../providers/admin-box-status-provider.service';
import { Dialog } from 'primeng/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminBackgroundTaskDataSourceService } from '@web/features/system';

@Component({
  selector: 'app-admin-box-list',
  imports: [TableComponent, AdminBoxListCustomCellTemplateComponent, Dialog, PrimengDialogDirective, ProgressBarComponent],
  templateUrl: './admin-box-list.component.html',
  styleUrl: './admin-box-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxListComponent implements OnInit {
  private readonly builder = inject(
    TableBuilderFactory<
      AdminBoxCollectionItem,
      AdminBoxCollectionFilter,
      AdminBoxListItemAction,
      AdminBoxListCollectionAction,
      AdminBoxListAction
    >,
  ).create();
  private readonly boxDataSource = inject(AdminBoxDataSourceService);
  private readonly backgroundTaskDataSource = inject(AdminBackgroundTaskDataSourceService);
  private readonly boxStatusProvider = inject(AdminBoxStatusProviderService);
  private readonly dateFormat = inject(DateFormatService);
  private readonly fileSaver = inject(FileSaverService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);
  private readonly table = inject(TableService);

  protected readonly undefinedProgress = -1;
  protected readonly backgroundTaskProgress = signal<number>(this.undefinedProgress);
  protected readonly completedOrdersSimulation = signal(false);
  protected readonly completedOrdersDialogVisibility = signal(false);

  protected readonly downloadLabelsDialogVisibility = signal(false);

  public readonly addVisibility = input<boolean>(false);

  protected config!: TableConfig<
    AdminBoxCollectionItem,
    AdminBoxCollectionFilter,
    AdminBoxListItemAction,
    AdminBoxListCollectionAction,
    AdminBoxListAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .actions((builder) => [
        builder
          .initRouterLink(AdminBoxListAction.Create)
          .label('Dodaj box kosmetyczny')
          .visibility(() => of(this.addVisibility()))
          .routerLink((provider) => provider.routerLink(Route.BoxCreate))
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminBoxListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.BoxDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initRouterLink(AdminBoxListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .icon(Icon.Pencil)
          .tooltip('Edycja')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.BoxEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),

        builder
          .initMore(AdminBoxListItemAction.More)
          .visibility((item) => this.shouldVisibleMore(item))
          .children((builder) => [
            builder
              .initCommand(AdminBoxListItemAction.CreateBoxSale)
              .visibility((item) => item.canBeBoxSaleCreated)
              .icon(Icon.Plus)
              .tooltip('Dodaj konfigurację sprzedaży')
              .command((item) => this.createBoxSale(item))
              .build(),

            builder
              .initRouterLink(AdminBoxListItemAction.PreviewBoxSale)
              .visibility((item) => item.canBeBoxSaleViewed)
              .icon(Icon.GearWideConnected)
              .tooltip('Podgląd konfiguracji sprzedaży')
              .routerLink(
                (provider) => (item) =>
                  provider.routerLink(Route.BoxSaleDetailsFromBoxes, {
                    [RouteParam.Guid]: item.boxSaleId!,
                  }),
              )
              .build(),

            builder
              .initCommand(AdminBoxListItemAction.CompleteOrders)
              .visibility((item) => item.canBeOrdersCompleted)
              .icon(Icon.BoxSeamFill)
              .tooltip('Oznacz zamówienia jako zrealizowane')
              .command((item) => this.completeOrders(item))
              .build(),

            builder
              .initCommand(AdminBoxListItemAction.SimulateCompleteOrders)
              .visibility((item) => item.canBeOrdersCompletedSimulated)
              .icon(Icon.BoxSeam)
              .tooltip('Oznacz zamówienia jako zrealizowane (Symulacja)')
              .command((item) => this.simulateCompleteOrders(item))
              .build(),

            builder
              .initCommand(AdminBoxListItemAction.DownloadLabels)
              .visibility((item) => item.canDownloadLabels)
              .icon(Icon.CloudDownloadFill)
              .tooltip('Pobierz etykiety')
              .command((item) => this.downloadLabels(item))
              .build(),

            builder
              .initCommand(AdminBoxListItemAction.Remove)
              .visibility((item) => item.canBeRemoved)
              .icon(Icon.Trash)
              .tooltip('Usuń')
              .command((item) => this.remove(item))
              .build(),
          ])
          .build(),
      ])
      .filters((builder) => [
        builder.initText('slug').build(),
        builder.initText('name').build(),
        builder
          .initSelect('status')
          .dictionary(() => this.boxStatusProvider.selectItems())
          .build(),
        builder.initDateRange('createdAt').build(),
      ])
      .columns((builder) => [
        builder
          .initCustom('featuredFileUrl')
          .width('9rem')
          .label('Zdjęcie główne')
          .build(),
        builder
          .initText('name')
          .label('Nazwa')
          .sortable(true)
          .sortKey('name')
          .filterable(true)
          .filterKey('name')
          .width('18rem')
          .build(),
        builder
          .initCurrency('price')
          .label('Cena')
          .sortable(true)
          .sortKey('price')
          .filterable(false)
          .width('8rem')
          .build(),
        builder
          .initCustom('availableQuantity')
          .label('Dostępna ilość')
          .sortable(true)
          .sortKey('availableQuantity')
          .filterable(false)
          .width('10rem')
          .build(),
        builder
          .initDateTime('createdAt')
          .label('Data utworzenia')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .width('12rem')
          .build(),
        builder
          .initCustom('status')
          .filterKey('status')
          .filterable(true)
          .label('Status')
          .sortKey('status')
          .sortable(true)
          .width('12rem')
          .build(),
        builder
          .initCustom('shopPreviewUrl')
          .width('20rem')
          .label('Adres URL do boxa')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminBoxCollectionFilter>,
        ): Observable<TableDataPayload<AdminBoxCollectionItem>> =>
          this.boxDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private createBoxSale(item: AdminBoxCollectionItem): void {
    this.boxDataSource
      .createSaleById({ id: item.id, name: item.name })
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private remove(item: AdminBoxCollectionItem): void {
    this.boxDataSource
      .removeById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private refresh(): void {
    this.table.refresh();
  }

  private shouldVisibleMore(item: AdminBoxCollectionItem): boolean {
    return [
      item.canBeBoxSaleCreated,
      item.canBeBoxSaleViewed,
      item.canBeRemoved,
      item.canBeOrdersCompleted,
      item.canBeOrdersCompletedSimulated,
      item.canDownloadLabels,
    ].some((value) => value);
  }

  private completeOrders(item: AdminBoxCollectionItem): void {
    this.confirmation.show((builder) =>
      builder
        .title('Potwierdzenie')
        .message('Czy na pewno chcesz się oznaczyć zamówienia związane z tym boxem jako zrealizowane?')
        .accept(() =>
          this.boxDataSource
            .completeOrdersById(item.id)
            .subscribe((id) => {
              this.disableCompletedOrderSimulation();
              this.showCompletedOrdersDialog();
              this.listenToCompletedOrdersTaskReady(id);
              this.refresh();
            }),
        )
        .build(),
    );
  }

  private simulateCompleteOrders(item: AdminBoxCollectionItem): void {
    this.confirmation.show((builder) =>
      builder
        .title('Potwierdzenie')
        .message('Czy na pewno chcesz przeprowadzić symulację oznaczania zamówień związanych z tym boxem jako zrealizowane?')
        .accept(() =>
          this.boxDataSource
            .simulateCompleteOrdersById(item.id)
            .subscribe((id) => {
              this.enableCompletedOrderSimulation();
              this.showCompletedOrdersDialog();
              this.listenToCompletedOrdersTaskReady(id);
              this.refresh();
            }),
        )
        .build(),
    );
  }

  private downloadLabels(item: AdminBoxCollectionItem): void {
    this.confirmation.show((builder) =>
      builder
        .title('Potwierdzenie')
        .message('Czy na pewno chcesz wygenerować paczkę z etykietami z tym boxem?')
        .accept(() =>
          this.boxDataSource
            .downloadLabels(item.id)
            .subscribe((id) => {
              this.showDownloadLabelDialog();
              this.listenToDownloadLabelsTaskReady(id);
              this.refresh();
            }),
        )
        .build(),
    );
  }

  private showCompletedOrdersDialog(): void {
    this.completedOrdersDialogVisibility.set(true);
  }

  private hideCompletedOrdersDialog(): void {
    this.completedOrdersDialogVisibility.set(false);
  }

  private enableCompletedOrderSimulation(): void {
    this.completedOrdersSimulation.set(true);
  }

  private disableCompletedOrderSimulation(): void {
    this.completedOrdersSimulation.set(false);
  }

  private showDownloadLabelDialog(): void {
    this.downloadLabelsDialogVisibility.set(true);
  }

  private hideDownloadLabelDialog(): void {
    this.downloadLabelsDialogVisibility.set(false);
  }

  private listenToCompletedOrdersTaskReady(id: Guid): void {
    const labelsFileName = `etykiety_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;
    const poolingTime = 5000;

    timer(0, poolingTime)
      .pipe(
        switchMap(() =>
          this.backgroundTaskDataSource.fetchStatus({ id }),
        ),
        tap((task) => {
          if (task.isDone()) {
            if (isNil(task.taskData)) {
              return;
            }

            if (this.completedOrdersSimulation()) {
              this.fileSaver
                .saveAsFromBase64(
                  `data:application/zip;base64,${task.taskData}`,
                  labelsFileName,
                )
                .subscribe();
            } else {
              window.location.replace(task.taskData);
            }
          } else {
            this.backgroundTaskProgress.set(task.progress);
          }
        }),
        takeWhile((task) => !task?.isDone(), true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (task) => {
          if (task.isDone()) {
            if (isNil(task.taskData)) {
              this.toast.show((builder) =>
                builder
                  .initWarning('Proces został zakończony. Brak pliku do pobrania ze względu na brak zamówień do przetworzenia.')
                  .build(),
              );
            } else {
              this.toast.show((builder) =>
                builder
                  .initSuccess('Za chwilę nastąpi pobieranie pliku.')
                  .build(),
              );
            }

            this.backgroundTaskProgress.set(this.undefinedProgress);
            this.hideCompletedOrdersDialog();
            this.disableCompletedOrderSimulation();
            this.refresh();
          }
        },
      });
  }

  private listenToDownloadLabelsTaskReady(id: Guid): void {
    const labelsFileName = `etykiety_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;
    const poolingTime = 10000;

    defer(() => this.backgroundTaskDataSource.fetchStatus({ id }))
      .pipe(
        expand((task) => {
          if (task.isDone()) {
            return EMPTY;
          }

          return timer(poolingTime).pipe(
            switchMap(() =>
              this.backgroundTaskDataSource.fetchStatus({ id }),
            ),
          );
        }),
        last(),
        switchMap((task) => {
          if (isNil(task.taskData)) {
            return EMPTY;
          }

          return this.fileSaver
            .saveAsFromBase64(
              `data:application/zip;base64,${task.taskData}`,
              labelsFileName,
            )
            .pipe(
              map(() => task),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (task) => {
          if (isNil(task.taskData)) {
            this.toast.show((builder) =>
              builder
                .initWarning('Proces został zakończony. Brak pliku do pobrania ze względu na brak zamówień do przetworzenia.')
                .build(),
            );
          } else {
            this.toast.show((builder) =>
              builder
                .initSuccess('Za chwilę nastąpi pobieranie pliku.')
                .build(),
            );
          }

          this.backgroundTaskProgress.set(this.undefinedProgress);
          this.hideDownloadLabelDialog();
          this.refresh();
        },
      });
  }
}
